import { useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar } from "@/components/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogOut, KeyRound, ChevronDown, Loader2, ImagePlus, Trash2, Pencil } from "lucide-react";

const ROLE_LABEL = {
  user: "User",
  manager: "Manager",
  super_manager: "Super Manager",
  super_admin: "Super Admin",
};

// Center-crop + resize to a small square JPEG data URL to keep the avatar tiny.
const fileToAvatar = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const size = 256;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

export default function ProfileMenu() {
  const { user, logout, updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const fileRef = useRef(null);

  const submitName = async (e) => {
    e.preventDefault();
    const nm = newName.trim();
    if (nm.length < 2) return toast.error("Name must be at least 2 characters");
    setNameSaving(true);
    try {
      const { data } = await api.post("/auth/name", { name: nm });
      updateUser({ ...user, name: data.name });
      toast.success("Name updated");
      setNameOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update name");
    } finally {
      setNameSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) return setError("New password must be at least 6 characters.");
    if (next !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setOpen(false);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToAvatar(file);
      const { data } = await api.post("/auth/avatar", { avatar: dataUrl });
      updateUser({ ...user, avatar: data.avatar });
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update photo");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      await api.post("/auth/avatar", { avatar: "" });
      updateUser({ ...user, avatar: null });
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Could not remove photo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="avatar-file-input" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2.5 rounded-lg px-1.5 py-1 hover:bg-secondary transition-colors"
            data-testid="profile-menu-trigger"
          >
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold leading-none">{user.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {ROLE_LABEL[user.role] || "User"}
              </p>
            </div>
            <Avatar src={user.avatar} name={user.name} className="h-9 w-9 text-sm" testid="profile-avatar" />
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); if (!uploading) fileRef.current?.click(); }}
            data-testid="change-photo-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-2" />}
            {user.avatar ? "Change photo" : "Add photo"}
          </DropdownMenuItem>
          {user.avatar && (
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); removePhoto(); }} data-testid="remove-photo-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Remove photo
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => { setNewName(user.name); setNameOpen(true); }} data-testid="open-edit-name">
            <Pencil className="h-4 w-4 mr-2" /> Edit name
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setOpen(true)} data-testid="open-change-password">
            <KeyRound className="h-4 w-4 mr-2" /> Change password
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={nameOpen} onOpenChange={setNameOpen}>
        <DialogContent data-testid="edit-name-dialog">
          <DialogHeader>
            <DialogTitle>Edit name</DialogTitle>
            <DialogDescription>Update your display name across the app.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitName} className="space-y-4" data-testid="edit-name-form">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full name</Label>
              <Input id="edit-name" value={newName} onChange={(e) => setNewName(e.target.value)} required data-testid="edit-name-input" />
            </div>
            <DialogFooter>
              <Button type="submit" className="rounded-lg" disabled={nameSaving} data-testid="save-name-btn">
                {nameSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save name"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="cur-pw">Current password</Label>
              <Input id="cur-pw" type="password" value={current}
                onChange={(e) => setCurrent(e.target.value)} required data-testid="current-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pw2">New password</Label>
              <Input id="new-pw2" type="password" value={next}
                onChange={(e) => setNext(e.target.value)} required data-testid="dialog-new-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="conf-pw2">Confirm new password</Label>
              <Input id="conf-pw2" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required data-testid="dialog-confirm-password-input" />
            </div>
            {error && <p className="text-sm text-destructive font-medium" data-testid="change-password-error">{error}</p>}
            <DialogFooter>
              <Button type="submit" className="rounded-lg" disabled={loading} data-testid="save-change-password-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
